import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { Menu, X, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/site";
import { SiteConfigProvider, useSiteConfig } from "@/context/SiteConfig";

const NAV_IDS = ["inicio", "nosotros", "soluciones", "industrias", "servicios", "ompec", "marcas", "contacto"];

const CUSTOM_VARS = (ct) => ({
  "--wk-primary": ct.primary, "--wk-secondary": ct.accent, "--wk-accent": ct.accent, "--wk-accent-hover": ct.accent,
  "--wk-cta": ct.cta, "--wk-cta-hover": ct.cta, "--wk-surface": ct.surface,
});

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="inline-flex items-center rounded-sm border border-white/25 overflow-hidden text-xs font-semibold" data-testid="lang-toggle">
      {["es", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
          className={`px-2.5 py-1 transition-colors ${lang === l ? "bg-[var(--wk-accent)] text-white" : "text-white/70 hover:text-white"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function PublicLayout({ children }) {
  return (
    <SiteConfigProvider>
      <PublicShell>{children}</PublicShell>
    </SiteConfigProvider>
  );
}

function PublicShell({ children }) {
  const { t } = useLang();
  const { site_theme, custom_theme } = useSiteConfig();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("inicio");
  const customStyle = site_theme === "custom" && custom_theme ? CUSTOM_VARS(custom_theme) : undefined;
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveId(e.target.id); }),
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    NAV_IDS.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="wk-public min-h-screen bg-[#FFFFFF] text-[#0F172A]" data-theme={site_theme} style={customStyle} data-testid="public-root">
      <header className={`sticky top-0 z-50 bg-[var(--wk-primary)] text-white transition-shadow ${scrolled ? "shadow-[0_8px_30px_rgba(0,0,0,0.25)]" : ""}`} data-testid="public-header">
        <div className="absolute bottom-0 left-0 h-0.5 bg-[var(--wk-accent)] transition-[width] duration-150" style={{ width: `${progress}%` }} data-testid="scroll-progress" />
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4 transition-[height] duration-300 ${scrolled ? "h-12" : "h-16"}`}>
          <button onClick={() => go("inicio")} className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
            <div className={`bg-white text-[var(--wk-primary)] grid place-items-center font-display font-extrabold rounded-sm transition-all duration-300 ${scrolled ? "w-7 h-7 text-sm" : "w-9 h-9"}`}>W</div>
            <div className="leading-none text-left">
              <div className="font-display font-extrabold tracking-tight text-[15px]">WEST KAC</div>
              <div className={`text-[9px] uppercase tracking-[0.18em] text-white/55 transition-all duration-300 overflow-hidden ${scrolled ? "max-h-0 opacity-0" : "max-h-4 opacity-100"}`}>Industrial Solutions</div>
            </div>
          </button>

          <nav className="hidden xl:flex items-center gap-6">
            {NAV_IDS.map((id) => (
              <button key={id} onClick={() => go(id)} data-testid={`nav-${id}`}
                className={`text-[13px] font-medium pb-0.5 border-b-2 transition-colors ${activeId === id ? "text-[var(--wk-accent)] border-[var(--wk-accent)]" : "text-white/75 hover:text-white border-transparent"}`}>
                {t.nav[id]}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block"><LangToggle /></div>
            <button onClick={() => go("contacto")} data-testid="header-quote-btn"
              className="hidden sm:inline-flex items-center bg-[var(--wk-cta)] hover:bg-[var(--wk-cta-hover)] text-white text-[13px] font-semibold px-4 py-2 rounded-sm transition-colors">
              {t.cta.quote}
            </button>
            <button className="xl:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-white/10 bg-[var(--wk-primary)] px-4 sm:px-6 py-4 flex flex-col gap-1" data-testid="mobile-menu">
            {NAV_IDS.map((id) => (
              <button key={id} onClick={() => go(id)} className={`text-left py-2.5 text-sm font-medium border-b border-white/5 flex items-center gap-2 ${activeId === id ? "text-[var(--wk-accent)]" : "text-white/80 hover:text-white"}`}>
                {activeId === id && <span className="w-1.5 h-1.5 rounded-full bg-[var(--wk-accent)]" />}{t.nav[id]}
              </button>
            ))}
            <div className="flex items-center justify-between pt-4">
              <LangToggle />
              <button onClick={() => go("contacto")} className="bg-[var(--wk-cta)] text-white text-sm font-semibold px-4 py-2 rounded-sm">{t.cta.quote}</button>
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <Footer />
    </div>
  );
}

function Footer() {
  const { t, lang } = useLang();
  const { whatsapp_phone, privacy_html, terms_html } = useSiteConfig();
  const [legalOpen, setLegalOpen] = useState(false);
  const go = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth" }); };
  const col = (title, items, ids) => (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 mb-4">{title}</div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <button key={i} onClick={() => go(ids?.[i] || "contacto")} className="block text-sm text-white/70 hover:text-white transition-colors text-left">{it}</button>
        ))}
      </div>
    </div>
  );
  return (
    <footer className="bg-[var(--wk-primary)] text-white" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid gap-10 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 bg-white text-[var(--wk-primary)] grid place-items-center font-display font-extrabold rounded-sm">W</div>
            <div className="font-display font-extrabold text-lg tracking-tight">WEST KAC COMPANY</div>
          </div>
          <div className="text-[var(--wk-accent)] font-display font-bold tracking-wide text-sm mb-3">SUPPLY. SOLVE. PROTECT.</div>
          <p className="text-sm text-white/60 leading-relaxed max-w-sm">{t.footer.about}</p>
          <p className="text-sm text-white/40 italic mt-4">{t.footer.tagline}</p>
        </div>
        {col(t.footer.colEmpresa, t.footer.empresa, ["nosotros", "industrias", "servicios", "marcas"])}
        {col(t.footer.colSol, t.footer.sol, ["soluciones", "soluciones", "soluciones", "ompec", "servicios"])}
        {col(t.footer.colOmpec, t.footer.ompec, ["ompec", "ompec", "ompec", "ompec", "ompec"])}
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/45">
          <div className="flex items-center gap-4">
            <span>© {new Date().getFullYear()} WEST KAC COMPANY. {t.footer.rights}</span>
            <button className="hover:text-white/80 transition-colors" onClick={() => setLegalOpen(true)} data-testid="footer-legal">{t.footer.legal}</button>
          </div>
          <div className="flex items-center gap-4">
            {whatsapp_phone && <a href={`https://wa.me/${whatsapp_phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="footer-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
            <Link to="/login" className="hover:text-white/80 transition-colors" data-testid="footer-portal">{t.cta.portal}</Link>
          </div>
        </div>
      </div>
      <LegalModal open={legalOpen} onClose={() => setLegalOpen(false)} privacy={privacy_html} terms={terms_html} lang={lang} />
    </footer>
  );
}

function LegalModal({ open, onClose, privacy, terms, lang }) {
  if (!open) return null;
  const tt = lang === "en"
    ? { title: "Privacy Notice & Terms", privacy: "Privacy Notice", terms: "Terms & Conditions", empty: "Content not available yet." }
    : { title: "Aviso de Privacidad y Términos", privacy: "Aviso de Privacidad", terms: "Términos y Condiciones", empty: "Contenido no disponible aún." };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="legal-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white text-[#0F172A] rounded-lg max-w-3xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-[var(--wk-primary)] text-white shrink-0">
          <h3 className="font-display font-bold text-lg">{tt.title}</h3>
          <button onClick={onClose} data-testid="legal-close" className="text-white/80 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-8">
          <section data-testid="legal-privacy">
            <h4 className="font-display font-bold text-[var(--wk-primary)] mb-3">{tt.privacy}</h4>
            {privacy ? <div className="text-sm leading-relaxed [&_a]:text-[var(--wk-accent)] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 space-y-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(privacy) }} /> : <p className="text-sm text-slate-500">{tt.empty}</p>}
          </section>
          <section data-testid="legal-terms">
            <h4 className="font-display font-bold text-[var(--wk-primary)] mb-3">{tt.terms}</h4>
            {terms ? <div className="text-sm leading-relaxed [&_a]:text-[var(--wk-accent)] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 space-y-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(terms) }} /> : <p className="text-sm text-slate-500">{tt.empty}</p>}
          </section>
        </div>
      </div>
    </div>
  );
}
